// ============================================================================
// COMMENTTIMELINE.JS - VERSIÓN LIMPIA COMPLETA CON NUEVAS CLASES CSS
// ============================================================================

import React, { useState, useRef } from 'react';
import {
  MessageSquare,
  Heart,
  Edit3,
  Trash2,
  Reply,
  Copy,
  Save,
  X
} from 'lucide-react';
import RichTextEditor, { RichTextViewer } from '../components/RichTextEditor';
import useToast from '../services/useToast';
import { resolveAvatarUrl } from './Avatar';
import LikePopover from './LikePopover';

import { parseServerDate } from '../utils/serverDate';
import { htmlToText } from '../utils/sanitizeHtml';
// Une nombres en lenguaje natural: "Ana", "Ana y Juan", "Ana, Juan y Pep".
function formatNameList(names) {
  if (!names || names.length === 0) return '';
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} y ${names[1]}`;
  return `${names.slice(0, -1).join(', ')} y ${names[names.length - 1]}`;
}

function CommentTimeline({
  comments = [],
  currentUser,
  onAddComment,
  onEditComment,
  onDeleteComment,
  onToggleLike,
  onStartReply,
  taskId
}) {
  const { showSuccess, showError, showWarning, confirm } = useToast();

  // Estados para interacciones
  const [editingComment, setEditingComment] = useState(null);
  const [editText, setEditText] = useState('');
  const [copiedComment, setCopiedComment] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');

  // Referencias
  const timelineRef = useRef(null);

  console.log('CommentTimeline - Comentarios recibidos:', comments);

  // ============================================================================
  // FUNCIONES HELPER
  // ============================================================================

  // Formatear fecha relativa
  const formatRelativeTime = (dateString) => {
    try {
      const date = parseServerDate(dateString);
      const now = new Date();
      const diffInSeconds = Math.floor((now - date) / 1000);

      if (diffInSeconds < 60) {
        return 'Hace un momento';
      } else if (diffInSeconds < 3600) {
        const minutes = Math.floor(diffInSeconds / 60);
        return `Hace ${minutes} minuto${minutes !== 1 ? 's' : ''}`;
      } else if (diffInSeconds < 86400) {
        const hours = Math.floor(diffInSeconds / 3600);
        return `Hace ${hours} hora${hours !== 1 ? 's' : ''}`;
      } else if (diffInSeconds < 2592000) {
        const days = Math.floor(diffInSeconds / 86400);
        return `Hace ${days} día${days !== 1 ? 's' : ''}`;
      } else {
        return date.toLocaleDateString('es-ES', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
    } catch {
      return 'Fecha inválida';
    }
  };

  // Fecha corta para la cabecera del comentario en compacto: «13 ago» (con año
  // solo si no es el actual). Lo reciente se queda en relativo («Hace 2 horas»).
  const formatShortTime = (dateString) => {
    try {
      const date = parseServerDate(dateString);
      const now = new Date();
      if ((now - date) / 1000 < 2592000) return formatRelativeTime(dateString);
      const opts = { day: 'numeric', month: 'short' };
      if (date.getFullYear() !== now.getFullYear()) opts.year = 'numeric';
      return date.toLocaleDateString('es-ES', opts).replace('.', '');
    } catch {
      return '';
    }
  };

  // Obtener iniciales del usuario
  const getUserInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  };

  // Detectar si es el autor del comentario (por id, no por nombre visible:
  // dos usuarios del mismo tenant pueden compartir nombre)
  const isCurrentUserAuthor = (comment) => {
    return String(comment?.userId) === String(currentUser?.id);
  };

  // Función para extraer texto plano del HTML
  const getPlainText = (html) => htmlToText(html);

  // Función para generar color de avatar basado en el nombre de usuario
  const getUserAvatarColor = (userName) => {
    if (!userName) return 'tcl-user-color-0';
    let hash = 0;
    for (let i = 0; i < userName.length; i++) {
      hash = userName.charCodeAt(i) + ((hash << 5) - hash);
    }
    return `tcl-user-color-${Math.abs(hash) % 10}`;
  };

  // ============================================================================
  // FUNCIONES DE COMENTARIOS
  // ============================================================================

  // Iniciar edición de comentario
  const startEditing = (comment) => {
    if (!isCurrentUserAuthor(comment)) {
      showWarning('Solo puedes editar tus propios comentarios');
      return;
    }
    
    setEditingComment(comment.id);
    setEditText(comment.texto);
  };

  // Cancelar edición
  const cancelEditing = () => {
    setEditingComment(null);
    setEditText('');
  };

  // Guardar edición
  const saveEdit = async () => {
    const plainText = getPlainText(editText);
    if (!plainText.trim()) {
      showWarning('El comentario no puede estar vacío');
      return;
    }

    try {
      if (onEditComment) {
        await onEditComment(editingComment, editText.trim());
      }
      setEditingComment(null);
      setEditText('');
      showSuccess('Comentario editado correctamente');
      console.log('Comentario editado exitosamente');
    } catch (error) {
      console.error('Error editando comentario:', error);
      showError(`Error al editar el comentario: ${error.message}`);
    }
  };

  // Eliminar comentario
  const handleDelete = async (comment) => {
    if (!isCurrentUserAuthor(comment)) {
      showWarning('Solo puedes eliminar tus propios comentarios');
      return;
    }

    const confirmDelete = await confirm('¿Estás seguro de que quieres eliminar este comentario?');
    if (!confirmDelete) return;

    try {
      if (onDeleteComment) {
        const result = await onDeleteComment(comment.id);
        if (result && !result.success) {
          showError(result.error || 'Error al eliminar el comentario');
          return;
        }
      }
      console.log('Comentario eliminado exitosamente');
    } catch (error) {
      console.error('Error eliminando comentario:', error);
      showError(`Error al eliminar el comentario: ${error.message}`);
    }
  };

  // Toggle like: delega en el padre, que persiste vía API y reconcilia el estado
  // (optimista + reversión en error). El corazón y el contador se derivan del
  // propio comentario, así que se actualizan solos al cambiar el estado del padre.
  const handleLike = async (comment) => {
    if (!onToggleLike) return;
    try {
      await onToggleLike(comment.id, currentUser?.id);
    } catch (error) {
      console.error('Error procesando like:', error);
      showError('Error al procesar el like');
    }
  };

  // Copiar texto del comentario
  const copyComment = async (comment) => {
    try {
      const plainText = getPlainText(comment.texto);
      await navigator.clipboard.writeText(plainText);
      setCopiedComment(comment.id);
      setTimeout(() => setCopiedComment(null), 2000);
      showSuccess('Comentario copiado al portapapeles');
      console.log('Comentario copiado al portapapeles');
    } catch (error) {
      console.error('Error copiando comentario:', error);
      showError('Error al copiar el comentario');
    }
  };

  // ============================================================================
  // FUNCIONES DE RESPUESTAS
  // ============================================================================

  // Iniciar respuesta: delegar al formulario principal si existe callback
  const startReply = (comment) => {
    if (onStartReply) {
      onStartReply(comment);
      return;
    }
    setReplyingTo(comment.id);
    setReplyText('');
  };

  // Cancelar respuesta
  const cancelReply = () => {
    setReplyingTo(null);
    setReplyText('');
  };

  // Enviar respuesta
  const saveReply = async () => {
    const plainText = getPlainText(replyText);
    if (!plainText.trim()) {
      showWarning('La respuesta no puede estar vacía');
      return;
    }

    try {
      console.log('Enviando respuesta con parent_id:', replyingTo);
      console.log('Texto de respuesta:', replyText.trim());
      
      if (onAddComment) {
        await onAddComment(replyText.trim(), replyingTo);
      }
      setReplyingTo(null);
      setReplyText('');
      showSuccess('Respuesta enviada correctamente');
      console.log('Respuesta enviada exitosamente');
    } catch (error) {
      console.error('Error enviando respuesta:', error);
      showError(`Error al enviar la respuesta: ${error.message}`);
    }
  };

  // ============================================================================
  // FUNCIÓN DE RENDERIZADO RECURSIVA
  // ============================================================================

  const renderComment = (comment, depth = 0, parentAuthor = null) => {
    const isEditing = editingComment === comment.id;
    const isReplying = replyingTo === comment.id;
    // isLiked y contador derivados del comentario persistido (no de estado local):
    // reflejan lo guardado al cargar y lo que el padre reconcilia tras cada toggle.
    const likesCount = comment.likesCount ?? comment.likes_count ?? 0;
    const isLiked = !!comment.isLiked ||
      (comment.likedBy || []).some(id => String(id) === String(currentUser?.id));
    // Corazón: popover con foto + nombre de quienes han dado like (LikePopover).
    // El texto equivalente va en aria-label para lectores de pantalla.
    const likedByPeople = comment.likedByPeople || [];
    const likedByNames = comment.likedByNames || likedByPeople.map(p => p.nombre);
    const extraLikers = Math.max(0, likesCount - likedByPeople.length);
    const likeVerb = (likesCount > 1 || likedByNames.length > 1) ? 'Les gusta a' : 'Le gusta a';
    const likeAriaLabel = likesCount > 0
      ? (likedByNames.length
          ? `${likeVerb} ${formatNameList(likedByNames)}${extraLikers > 0 ? ` y ${extraLikers} más` : ''}`
          : `${likesCount} me gusta`)
      : 'Me gusta';
    const isAuthor = isCurrentUserAuthor(comment);
    const isCopied = copiedComment === comment.id;
    const maxDepth = 3;

    return (
      <div key={comment.id} className="tcl-comment-wrapper">
        <div
          className={`tcl-comment-item ${isAuthor ? 'tcl-own-comment' : ''} tcl-depth-${Math.min(depth, maxDepth)}`}
        >
          {/* Avatar */}
          <div className="tcl-comment-avatar">
            <div className={`tcl-avatar ${isAuthor ? 'tcl-own-avatar' : getUserAvatarColor(comment.autor)} ${comment.autorAvatar ? 'tcl-avatar-has-photo' : ''}`}>
              {getUserInitials(comment.autor)}
              {comment.autorAvatar && (
                <img
                  src={resolveAvatarUrl(comment.autorAvatar)}
                  alt={comment.autor}
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              )}
            </div>
          </div>

          {/* Contenido del comentario */}
          <div className="tcl-comment-content">
            {/* Indicador de respuesta */}
            {depth > 0 && parentAuthor && (
              <div className="tcl-reply-context">
                <Reply size={11} />
                <span>En respuesta a <strong>{parentAuthor}</strong></span>
              </div>
            )}
            {/* Header del comentario */}
            <div className="tcl-comment-header">
              <div className="tcl-comment-author-info">
                <div className="tcl-comment-author-line">
                  <span className="tcl-comment-author">{comment.autor}</span>
                  <span className="tcl-comment-time u-hide-mobile">{formatRelativeTime(comment.fecha)}</span>
                  <span className="tcl-comment-time u-only-mobile" title={formatRelativeTime(comment.fecha)}>
                    {formatShortTime(comment.fecha)}
                  </span>
                </div>
                {comment.editado && (
                  <span className="tcl-comment-edited">(editado)</span>
                )}
              </div>
              
              {/* Acciones secundarias - ABAJO DERECHA */}
              <div className="tcl-secondary-actions">
                <button
                  className="tcl-reply-button"
                  onClick={() => startReply(comment)}
                  title="Responder"
                >
                  <Reply size={12} />
                  Responder
                </button>

                {/* Botón de copiar directo */}
                <button
                  className={`tcl-copy-button ${isCopied ? 'tcl-copied' : ''}`}
                  onClick={() => copyComment(comment)}
                  title={isCopied ? 'Copiado!' : 'Copiar comentario'}
                >
                  <Copy size={12} />
                </button>
                {/* Botón de like - MÁS ARRIBA */}
              <LikePopover people={likedByPeople} extra={extraLikers}>
                <button
                  className={`tcl-like-button ${isLiked ? 'tcl-liked' : ''}`}
                  onClick={() => handleLike(comment)}
                  aria-label={likeAriaLabel}
                >
                  <Heart size={14} fill={isLiked ? 'currentColor' : 'none'} />
                  {likesCount > 0 && (
                    <span className="tcl-like-count">{likesCount}</span>
                  )}
                </button>
              </LikePopover>

                {/* Botones de editar y eliminar solo para el autor */}
                {isAuthor && (
                  <>
                    <button
                      className="tcl-edit-button"
                      onClick={() => startEditing(comment)}
                      title="Editar comentario"
                    >
                      <Edit3 size={12} />
                    </button>
                    <button
                      className="tcl-delete-button"
                      onClick={() => handleDelete(comment)}
                      title="Eliminar comentario"
                    >
                      <Trash2 size={12} />
                    </button>
                  </>
                )}
               </div>
            </div>

            {/* Texto del comentario o editor */}
            <div className="tcl-comment-body">
              {isEditing ? (
                <div className="tcl-comment-editor">
                  <RichTextEditor
                    value={editText}
                    onChange={setEditText}
                    height="150px"
                    placeholder="Edita tu comentario..."
                    taskId={taskId}
                    enableMentions
                  />
                  <div className="tcl-edit-actions">
                    <button className="tcl-btn-save" onClick={saveEdit}>
                      <Save size={14} />
                      Guardar
                    </button>
                    <button className="tcl-btn-cancel" onClick={cancelEditing}>
                      <X size={14} />
                      Cancelar
                    </button>
                  </div>
                </div>
              ) : (
                // Si el comentario contiene HTML, usar RichTextViewer
                comment.texto.includes('<') ? (
                  <RichTextViewer content={comment.texto} className="tcl-comment-text" />
                ) : (
                  <p className="tcl-comment-text">{comment.texto}</p>
                )
              )}
            </div>

            {/* Formulario de respuesta */}
            {isReplying && (
              <div className="tcl-reply-form">
                <div className="tcl-reply-header">
                  <div className={`tcl-user-avatar-small ${getUserAvatarColor(currentUser?.nombre || currentUser?.name)} ${currentUser?.avatar ? 'tcl-avatar-has-photo' : ''}`}>
                    {getUserInitials(currentUser?.nombre || currentUser?.name)}
                    {currentUser?.avatar && (
                      <img
                        src={resolveAvatarUrl(currentUser.avatar)}
                        alt={currentUser?.nombre || currentUser?.name}
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                    )}
                  </div>
                  <span className="tcl-replying-to">Respondiendo a {comment.autor}</span>
                </div>
                
                <RichTextEditor
                  value={replyText}
                  onChange={setReplyText}
                  height="100px"
                  placeholder="Escribe tu respuesta... Usa @ para mencionar"
                  taskId={taskId}
                  enableMentions
                />
                
                <div className="tcl-reply-actions">
                  <button className="tcl-btn-save" onClick={saveReply}>
                    <Reply size={14} />
                    Responder
                  </button>
                  <button className="tcl-btn-cancel" onClick={cancelReply}>
                    <X size={14} />
                    Cancelar
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* RESPUESTAS ANIDADAS - PARTE CRUCIAL */}
        {comment.respuestas && comment.respuestas.length > 0 && depth < maxDepth && (
          <div className="tcl-comment-replies">
            {comment.respuestas.map(reply => renderComment(reply, depth + 1, comment.autor))}
          </div>
        )}
      </div>
    );
  };

  // ============================================================================
  // RENDER PRINCIPAL
  // ============================================================================

  if (comments.length === 0) {
    return (
      <div className="tcl-timeline-empty">
        <div className="tcl-empty-content">
          <MessageSquare size={14} className="tcl-empty-icon" />
          <span>Sin comentarios aún</span>
        </div>
      </div>
    );
  }

  return (
    <div className="tcl-comment-timeline" ref={timelineRef}>
      {/* Lista de comentarios */}
      <div className="tcl-comments-list">
        {comments.map(comment => renderComment(comment, 0))}
      </div>
    </div>
  );
}

export default CommentTimeline;